import { useEffect, useRef, useState } from 'react';
import type { FeedMessage, ObjectiveView } from '@head-of-trading/contracts';
import { Button } from '@head-of-trading/ui';
import { useLanguage } from '../../i18n/context.js';
import { getTerminalCopy } from '../../i18n/terminal.js';
import { formatClock } from '../../lib/format.js';

const portraitPath = (portrait: NonNullable<FeedMessage['portrait']>) => `/assets/portraits/${portrait}.webp`;

function Avatar({ message }: { readonly message: FeedMessage }) {
  if (!message.portrait) return <span className={`feed-system-icon feed-system-icon--${message.category.toLowerCase()}`}>{message.category === 'RISK' ? '!' : message.category === 'MARKET' ? '⌁' : 'i'}</span>;
  return <img className="feed-avatar" src={portraitPath(message.portrait)} alt="" width="38" height="38" />;
}

export function DeskFeed({ messages, onFomc }: { readonly messages: readonly FeedMessage[]; readonly onFomc: () => void }) {
  const { language } = useLanguage();
  const copy = getTerminalCopy(language).deskFeed;
  const viewport = useRef<HTMLDivElement>(null);
  const previousLength = useRef(messages.length);
  const [following, setFollowing] = useState(true);
  const [unseen, setUnseen] = useState(0);
  useEffect(() => {
    const added = Math.max(0, messages.length - previousLength.current);
    previousLength.current = messages.length;
    if (following) {
      requestAnimationFrame(() => viewport.current?.scrollTo({ top: viewport.current.scrollHeight, behavior: 'smooth' }));
      setUnseen(0);
    } else if (added) setUnseen((value) => value + added);
  }, [messages.length, following]);
  useEffect(() => { viewport.current?.scrollTo({ top: viewport.current.scrollHeight }); }, []);
  const scroll = () => {
    const node = viewport.current;
    if (!node) return;
    const atBottom = node.scrollHeight - node.scrollTop - node.clientHeight < 28;
    setFollowing(atBottom);
    if (atBottom) setUnseen(0);
  };
  const resume = () => { setFollowing(true); setUnseen(0); viewport.current?.scrollTo({ top: viewport.current.scrollHeight, behavior: 'smooth' }); };
  return (
    <section data-testid="desk-feed" className="desk-feed terminal-panel" aria-label={copy.aria}>
      <header className="terminal-panel__head"><div><span>{copy.eyebrow}</span><h2>{copy.title}</h2></div><div className="feed-categories"><span>{copy.categories.DESK}</span><span>{copy.categories.MARKET}</span><span>{copy.categories.RISK}</span><span>{copy.categories.SYSTEM}</span></div></header>
      <div className="feed-scroll" ref={viewport} onScroll={scroll} tabIndex={0} aria-live="polite">
        {messages.map((message) => <article className={`feed-message feed-message--${message.category.toLowerCase()} ${message.emphasis ? 'is-emphasis' : ''}`} key={message.id}>
          <Avatar message={message} />
          <div><header><strong>{message.author ?? copy.categories[message.category]}</strong>{message.role && <span>{message.role}</span>}<time>{formatClock(message.simulatedAt)}</time></header><p>{message.text}</p>{message.action?.type === 'OPEN_FOMC_WATCH' && <Button data-testid="fomc-watch-trigger" variant="secondary" size="sm" onClick={onFomc}>{message.action.label || copy.openFomc} ↗</Button>}</div>
        </article>)}
        {messages.length === 0 && <div className="feed-empty"><span className="live-dot" /><p>{copy.empty}</p></div>}
      </div>
      {unseen > 0 && <button className="new-messages" onClick={resume}>{copy.newMessages(unseen)}</button>}
    </section>
  );
}

export function Objective({ objective }: { readonly objective: ObjectiveView }) {
  const { language } = useLanguage();
  const copy = getTerminalCopy(language).objective;
  return (
    <section className="objective terminal-panel" aria-labelledby="objective-title">
      <header><span>{copy.eyebrow}</span><time>{copy.deadline(formatClock(objective.deadline))}</time></header>
      <h2 id="objective-title">{objective.title}</h2><p>{objective.instruction}</p>
      <dl><div><dt>{copy.target}</dt><dd>{objective.target}</dd></div>{objective.tolerance && <div><dt>{copy.tolerance}</dt><dd>{objective.tolerance}</dd></div>}</dl>
      {objective.hardConstraints.length > 0 && <div className="hard-constraints"><strong>{copy.hardConstraint}</strong>{objective.hardConstraints.map((constraint) => <span key={constraint}>{constraint}</span>)}</div>}
    </section>
  );
}
